// -----------------------------------------------------------------------------
// React Query hooks for layout variants (Phase 8 Wave 8.E).
// -----------------------------------------------------------------------------

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  addLayoutVariant,
  LayoutVariantQueryKeys,
  listLayoutVariants,
  removeLayoutVariant,
  type AddLayoutVariantBody,
  type LayoutVariantResponse,
} from './api';

export function useLayoutVariants(slug: string | undefined): UseQueryResult<LayoutVariantResponse[]> {
  return useQuery({
    queryKey: slug !== undefined ? LayoutVariantQueryKeys.list(slug) : ['layout-variants', '__skip__'],
    queryFn: ({ signal }) => listLayoutVariants(slug as string, signal),
    enabled: slug !== undefined,
  });
}

export interface AddVariantVariables {
  slug: string;
  body: AddLayoutVariantBody;
}

export function useAddLayoutVariant(): UseMutationResult<LayoutVariantResponse, Error, AddVariantVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, body }) => addLayoutVariant(slug, body),
    onSuccess: (_, { slug }) => {
      void queryClient.invalidateQueries({ queryKey: LayoutVariantQueryKeys.list(slug) });
    },
  });
}

export interface RemoveVariantVariables {
  slug: string;
  variantId: string;
}

export function useRemoveLayoutVariant(): UseMutationResult<null, Error, RemoveVariantVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ slug, variantId }) => removeLayoutVariant(slug, variantId),
    onSuccess: (_, { slug }) => {
      void queryClient.invalidateQueries({ queryKey: LayoutVariantQueryKeys.list(slug) });
    },
  });
}
